import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'deploying'
  | 'deployed'
  | 'installed';

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  v0ProjectId: string;
  v0ChatId: string;
  v0LatestVersionId: string;
  v0DeploymentId: string;
  v0ChatUrl: string;
  v0DemoUrl: string;
  deploymentUrl: string;
  installedAppId: string;
  installedModuleId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAppBuilderInput {
  name: string;
  description: string;
  prompt: string;
  icon: string;
  instructions?: string;
  branch?: string;
}

export interface SendAppBuilderMessageInput {
  id: string;
  message: string;
}

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'open_in_v0'
  | 'send_followup';

export interface AppBuilderMessage {
  id: string;
  content: string;
  type: string;
  role: 'user' | 'assistant' | string;
  finishReason?: string;
  actionHints?: AppBuilderMessageActionHint[];
}

export interface AppBuilderMessagesResponse {
  data: AppBuilderMessage[];
}

interface SendMessageResponse {
  messageId?: string;
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createAppBuilder: build.mutation<AppBuilder, CreateAppBuilderInput>({
      query: (body) => ({
        path: '/app-builders',
        method: ApiMethods.post,
        options: { body },
      }),
      invalidatesTags: [getAppBuilderListTag()],
    }),
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    sendAppBuilderMessage: build.mutation<
      SendMessageResponse,
      SendAppBuilderMessageInput
    >({
      query: ({ id, message }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.post,
        options: { body: { message } },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    listAppBuilderMessages: build.query<
      AppBuilderMessagesResponse,
      {
        id: string;
        limit?: number;
        cursor?: string;
      }
    >({
      query: ({ id, limit, cursor }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            ...(limit ? { limit: String(limit) } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    resumeAppBuilderMessage: build.mutation<
      AppBuilderMessage,
      { id: string; messageId: string }
    >({
      query: ({ id, messageId }) => ({
        path: `/app-builders/${id}/messages/${messageId}/resume`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.appBuilders],
    }),
  }),
});

export const {
  useCreateAppBuilderMutation,
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useSendAppBuilderMessageMutation,
  useListAppBuilderMessagesQuery,
  useResumeAppBuilderMessageMutation,
  useDeployAppBuilderMutation,
  useDeleteAppBuilderMutation,
} = appBuilderApi;
